"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

const VISITOR_KEY = "seexr_analytics_visitor_id";
const SESSION_KEY = "seexr_analytics_session";
const LAST_VIEW_KEY = "seexr_analytics_last_view";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function makeId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
}

function getVisitorId() {
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = makeId();
    window.localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSessionId() {
  const now = Date.now();
  try {
    const saved = JSON.parse(window.localStorage.getItem(SESSION_KEY) || "null") as
      | { id?: string; lastSeen?: number }
      | null;
    if (
      saved?.id &&
      typeof saved.lastSeen === "number" &&
      now - saved.lastSeen < SESSION_TIMEOUT_MS
    ) {
      window.localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ id: saved.id, lastSeen: now }),
      );
      return saved.id;
    }
  } catch {
    // Replace malformed local state below.
  }

  const id = makeId();
  window.localStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: now }));
  return id;
}

export function TrafficAnalytics() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;

    const host = window.location.hostname.toLowerCase();
    const isSeexr = /(^|\.)seexr\.(dev|co\.kr)$/.test(host);
    const isLocal = host === "localhost" || host === "127.0.0.1";
    if (!isSeexr && !isLocal) return;

    const now = Date.now();
    const lastView = window.sessionStorage.getItem(LAST_VIEW_KEY);
    if (lastView) {
      try {
        const parsed = JSON.parse(lastView) as { path?: string; at?: number };
        if (
          parsed.path === pathname &&
          typeof parsed.at === "number" &&
          now - parsed.at < 1500
        ) {
          return;
        }
      } catch {
        // Ignore malformed de-duplication state.
      }
    }
    window.sessionStorage.setItem(
      LAST_VIEW_KEY,
      JSON.stringify({ path: pathname, at: now }),
    );

    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:5016";
    const payload = {
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      host,
      path: pathname,
      referrer: document.referrer || undefined,
      title: document.title || undefined,
      language: navigator.language || undefined,
      screenWidth: Math.round(window.screen.width || 0),
    };

    void fetch(apiUrl.replace(/\/$/, "") + "/api/analytics/page-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "omit",
      keepalive: true,
    }).catch(() => {
      // Analytics must never interfere with product UX.
    });
  }, [pathname]);

  return null;
}
